import { cn } from '@/shared/lib/cn';

/**
 * Pill for `04.ComprobantePagos.Status_CP` (internal name `Status_P`).
 * Same palette convention as StatusRoBadge — short labels, uppercase,
 * pastel tones so the Carga de pagos table stays scannable.
 */
type Tone = 'green' | 'yellow' | 'orange' | 'slate';

const STATUS_TONES: Record<string, Tone> = {
  Confirmado: 'green',
  'Pendiente de Confirmacion': 'yellow',
  'Pendiente de Cobro': 'orange',
  Anulado: 'slate',
};

const SHORT_LABELS: Record<string, string> = {
  Confirmado: 'CONFIRMADO',
  'Pendiente de Confirmacion': 'PEND. CONFIRMACIÓN',
  'Pendiente de Cobro': 'PEND. COBRO',
  Anulado: 'ANULADO',
};

const TONE_CLASSES: Record<Tone, string> = {
  green: 'bg-emerald-100 text-emerald-700',
  yellow: 'bg-amber-100 text-amber-700',
  orange: 'bg-orange-100 text-orange-700',
  slate: 'bg-slate-100 text-slate-600',
};

/**
 * Forma compartida de la píldora. La exportamos para que otros tags de la
 * misma fila (p. ej. el de Facturación Previa) tengan idéntico tamaño,
 * padding y tipografía en vez de copiar las clases y quedar desalineados.
 */
export const PILL_BASE =
  'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-semibold tracking-wide';

export function StatusCPBadge({
  status,
  className,
  full = false,
}: {
  status: string | null | undefined;
  className?: string;
  /** When true, render the full status text (e.g. "Pendiente de Cobro")
   *  instead of the abbreviated form ("PEND. COBRO"). Useful when the
   *  badge has more horizontal room. */
  full?: boolean;
}) {
  const raw = status || '—';
  const label = full ? raw : SHORT_LABELS[raw] ?? raw.toUpperCase();
  const tone = STATUS_TONES[raw] ?? 'slate';
  return (
    <span
      className={cn(
        PILL_BASE,
        full ? 'normal-case' : 'uppercase',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {label}
    </span>
  );
}
